<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import { updateDataField, type ApiField } from '../lib/data-tables-api';
  import Icon from './Icon.svelte';
  import FieldTypeIcon from './FieldTypeIcon.svelte';

  let {
    slug,
    fields,
    onChanged,
  }: {
    /** Table slug — 'users' / 'payments' / custom. */
    slug: string;
    /** Усі fields catalog (built-in + custom) які можна потенційно показати у LIST.
     *  ВЖЕ profilter-ovані по min_view_role у caller. */
    fields: ApiField[];
    /** Після save оновляться show_in_list — caller робить refetch schema. */
    onChanged?: () => void;
  } = $props();

  let open = $state(false);
  let busy = $state<string | null>(null);

  /** Visible-у-LIST fields сортовані за position + builtIn-first. */
  const sorted = $derived(
    [...fields]
      .sort((a, b) => {
        if (a.builtIn !== b.builtIn) return a.builtIn ? -1 : 1;
        return (a.position ?? 0) - (b.position ?? 0);
      }),
  );

  async function toggle(field: ApiField) {
    if (busy) return;
    const next = !(field.show_in_list !== false);
    busy = field.id;
    try {
      await updateDataField(slug, field.id, { showInList: next });
      onChanged?.();
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    } finally {
      busy = null;
    }
  }
</script>

<div class="relative">
  <button
    type="button"
    onclick={() => (open = !open)}
    class="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-semibold transition-colors
      {open
        ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
        : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
    title="Налаштувати видимість колонок"
  >
    <span class="flex"><Icon name="columns" size={15} /></span>
    <span class="hidden sm:inline">Видимість колонок</span>
    <Icon name="chevronDown" size={14} class="text-[var(--color-text-muted)]" />
  </button>

  {#if open}
    <div class="fixed inset-0 z-30" role="presentation" onclick={() => (open = false)}></div>

    <div
      class="absolute left-0 top-full mt-1.5 z-40 w-72 rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl overflow-hidden"
      onclick={(e) => e.stopPropagation()}
      role="menu"
    >
      <!-- Header — у стилі дропдауну «Вид списком» -->
      <div class="flex items-center gap-3 px-3.5 py-3 bg-purple-50 text-[var(--color-pu-3)] border-b border-[var(--color-border-3)]">
        <span class="w-6 flex justify-center shrink-0"><Icon name="columns" size={16} /></span>
        <span class="flex-1 text-[12.5px] font-semibold">Видимість колонок</span>
        <button type="button" onclick={() => (open = false)} class="inline-flex items-center text-[var(--color-pu-3)] hover:opacity-70"><Icon name="close" size={15} /></button>
      </div>

      <div class="max-h-[320px] overflow-y-auto py-1">
        {#each sorted as f (f.id)}
          {@const isVisible = f.show_in_list !== false}
          <label
            class="flex items-center gap-3 px-3.5 py-2 text-[12.5px] hover:bg-[var(--color-bg-3)] cursor-pointer transition-colors"
            class:opacity-50={busy === f.id}
          >
            <input
              type="checkbox"
              checked={isVisible}
              onchange={() => void toggle(f)}
              disabled={busy === f.id}
              class="h-4 w-4 shrink-0 accent-[var(--color-pu)]"
            />
            <span class="w-5 flex justify-center shrink-0 text-[var(--color-text-muted)]"><FieldTypeIcon type={f.type} size={15} /></span>
            <span class="flex-1 truncate font-medium">{f.label}</span>
          </label>
        {/each}
      </div>

      <div class="px-3.5 py-2 border-t border-[var(--color-border-3)] text-[10px] text-[var(--color-text-muted-2)]">
        Зміни глобальні (для всіх юзерів). Швидкий shortcut до Settings → поле.
      </div>
    </div>
  {/if}
</div>
